import { ChatFileUploadHeadersDto, FileRecord, FileTypeEnum, IOStatusEnum, isDefined, MediaMetaData } from '@matap/shared';
import { EventEmitter } from '@/webrtc/utils/event.emitter';
import { initFactory } from '../utils/job.manager';
import { FileStorage } from './abstract.file.system';
import { getFileSystem } from './index';
import moment from 'jalali-moment';

const TAG = '[file-asset]';

export interface FileAssetInfo {
  state: IOStatusEnum;
  localAddress?: string;
  progress: number;
  fileName: string;
  source: FileAsset;
}

function createCacheKey(id: string, storageName: string) {
  return `${id}-${storageName}`;
}

export interface Uploadable {
  mime: string;
  fileName: string;
  localAddress: string;
  mediaMetaData: MediaMetaData;
  type: FileTypeEnum;
}

export class FileAsset extends EventEmitter<'state-change' | 'evicted' | 'error'> {
  private static readonly cache = new Map<string, { asset?: FileAsset; promise: Promise<FileAsset> }>();

  private readonly emitFactory = initFactory(`emit-factory`, { capacity: 2, maxCapPolicy: 'drop_oldest', rateLimit: 500 });
  private mime: string;
  private fileName: string;
  public mediaMetaData: MediaMetaData;
  private storage: FileStorage;
  private knownLocalAddress: string;
  private uploaded?: boolean;
  public type: FileTypeEnum;
  public thumbnail?: string;

  private references = 0;
  private mode: 'uploadable' | 'downloadable';

  private remoteUrl?: string;
  private progress: number;
  private downloadFailed = false;
  private inited = false;
  private released = false;
  public events: string[] = []; // Changed to public so you can log it externally if needed

  private trace(action: string) {
    const log = `[${moment().format('HH:mm:ss:SSS')}] ${action}`;
    this.events.push(log);
    // Optional: console.log(TAG, this.remoteUrl || this.fileName, log);
  }

  public static create(data: Uploadable | FileRecord, temporary = false): Promise<FileAsset> {
    const storage = getFileSystem(temporary);

    let key: string;
    if ('_id' in data && 'uploadDate' in data) {
      key = createCacheKey(data._id, storage.name);
      if (FileAsset.cache.has(key)) {
        const cached = FileAsset.cache.get(key);
        return cached?.promise.then((res) => {
          res.retain();
          return res;
        });
      }
    }

    const asset = new FileAsset(key, temporary);

    // asset.retain(); instance the default references is set to 1

    const initPromise = (async () => {
      try {
        await asset.initialize(data);
        if (key) {
          FileAsset.cache.set(key, { asset, promise: initPromise });
        }
        return asset;
      } catch (error) {
        if (key) FileAsset.cache.delete(key);
        asset.release('initialization failed', true);
        throw error;
      }
    })();

    if (key) {
      FileAsset.cache.set(key, { asset: null, promise: initPromise });
    }

    return initPromise.then((res) => {
      res.retain();
      return res;
    });
  }

  private constructor(
    private cacheKey: string,
    readonly isTemporary: boolean,
  ) {
    super();
    this.storage = getFileSystem(isTemporary);
    this.on('error', (error) => this.trace(`ERROR: ${error}`));
  }

  protected async initialize(data: Uploadable | FileRecord) {
    if (this.inited) {
      this.emit('error', 'already inited', this);
      throw new Error('already inited');
    }
    this.trace('initialize() started');

    if ('_id' in data && 'uploadDate' in data) {
      this.mode = 'downloadable';
      this.mediaMetaData = data.metadata.mediaMetaData;
      this.type = data.metadata.type;
      this.mime = data.metadata.contentType;
      this.fileName = data.filename;
      this.remoteUrl = data.metadata.url;

      this.knownLocalAddress = await this.storage.getByUrl(this.remoteUrl);
      this.progress = Math.max(0, this.storage.downloadProgress(this.remoteUrl));
    } else {
      this.mode = 'uploadable';
      this.mime = data.mime;
      this.fileName = data.fileName;
      this.type = data.type;
      this.uploaded = false;
      this.mediaMetaData = data.mediaMetaData;

      this.knownLocalAddress = data.localAddress;
      this.progress = Math.max(0, this.storage.uploadProgress(this.knownLocalAddress));
    }

    this.registerListeners();
    this.inited = true;
    this.trace(`initialize() finished | knownLocalAddress: ${this.knownLocalAddress} | remoteUrl: ${this.remoteUrl}`);
    return this;
  }

  private emitStateChange() {
    this.emitFactory.schedule(async () => {
      if (!this.inited) {
        return;
      }
      const info = this.info();
      this.trace('emitted state change');
      this.emit('state-change', info);
    });
  }

  public async get(download = true): Promise<string | undefined> {
    this.trace(`get() called | download: ${download} | knownLocalAddress: ${!!this.knownLocalAddress}`);
    if (!this.inited) {
      this.emit('error', 'not inited yet at get()', this);
      throw new Error('not inited yet');
    }
    if (!this.knownLocalAddress && download && this.remoteUrl && !this.downloadFailed) {
      this.trace(`get() triggering download for remoteUrl: ${this.remoteUrl}`);
      return this.storage.download(this.remoteUrl);
    }
    return this.knownLocalAddress;
  }

  public forceDownload() {
    this.trace('forceDownload() called');
    this.downloadFailed = false;
    return this.get(true);
  }

  public async upload(url: string, headers?: Omit<ChatFileUploadHeadersDto, 'metadata' | 'type'>) {
    this.trace(`upload() called | targetUrl: ${url}`);
    if (!this.inited) {
      this.emit('error', 'not inited yet at upload()', this);
      throw new Error('not inited yet');
    }
    const status = this.status();
    if (status !== IOStatusEnum.WAITING_FOR_UPLOAD) {
      return;
    }
    if (!this.knownLocalAddress) {
      return undefined;
    }
    return this.storage.upload(this.knownLocalAddress, url, this.mime, this.fileName, { type: this.type, metadata: JSON.stringify(this.mediaMetaData), ...headers });
  }

  private isDownloading() {
    return this.isDownloadable() && this.storage.downloadProgress(this.remoteUrl) !== -1;
  }

  private isUploading() {
    return this.isUploadable() && this.storage.uploadProgress(this.knownLocalAddress) !== -1;
  }

  private isUploadable() {
    return this.mode === 'uploadable';
  }

  private isDownloadable() {
    return this.mode === 'downloadable';
  }

  public status(): IOStatusEnum {
    if (!this.inited) {
      this.emit('error', 'not inited yet at status()', this);
      throw new Error('not inited yet');
    }
    if (this.isDownloading()) {
      return IOStatusEnum.DOWNLOADING;
    }

    if (this.isUploading()) {
      return IOStatusEnum.UPLOADING;
    }

    if (isDefined(this.uploaded) && !this.uploaded) {
      return IOStatusEnum.WAITING_FOR_UPLOAD;
    }

    if (this.knownLocalAddress) {
      return IOStatusEnum.READY;
    }

    if (this.remoteUrl) {
      return IOStatusEnum.WAITING_FOR_DOWNLOAD;
    }

    return IOStatusEnum.UNKNOWN;
  }

  public info(): FileAssetInfo {
    if (!this.inited) {
      this.emit('error', 'not inited yet at info()', this);
      throw new Error('not inited yet');
    }
    const status = this.status();
    return { state: status, localAddress: this.knownLocalAddress, fileName: this.fileName, progress: this.progress, source: this };
  }

  protected mDownloadedListener = (remoteUrl: string, localAddress: string) => {
    if (this.remoteUrl !== remoteUrl) {
      return;
    }
    this.trace(`mDownloadedListener | localAddress: ${localAddress}`);
    this.knownLocalAddress = localAddress;
    this.downloadFailed = false;
    this.emitStateChange();
  };

  private mDownloadingListener = (remoteUrl: string, progress: number) => {
    if (this.remoteUrl !== remoteUrl) {
      return;
    }
    this.trace(`mDownloadingListener | progress: ${progress}`);
    if (this.knownLocalAddress) {
      this.emit('error', 'should not download while local address already exists', this);
      throw new Error('should not download while local address already exists!');
    }
    this.progress = progress;
    this.emitStateChange();
  };

  private mDownloadErrorListener = (remoteUrl: string, error: any) => {
    if (this.remoteUrl !== remoteUrl) {
      return;
    }
    this.emit('error', `mDownloadErrorListener | error: ${error?.message || error}`, this);
    this.progress = 0;
    this.downloadFailed = true;
    this.emitStateChange();
  };

  private mUploadedListener = async (localAddress: string, fileRecord: FileRecord) => {
    if (this.knownLocalAddress !== localAddress) {
      return;
    }
    this.trace(`mUploadedListener | new remoteUrl: ${fileRecord.metadata.url}`);
    this.progress = 0;
    this.remoteUrl = fileRecord.metadata.url;
    this.mediaMetaData = fileRecord.metadata.mediaMetaData;
    this.mime = fileRecord.metadata.contentType;
    this.uploaded = true;

    this.cacheKey = createCacheKey(fileRecord._id, this.storage.name);
    FileAsset.cache.set(this.cacheKey, { asset: this, promise: Promise.resolve(this) });

    this.emitStateChange();
  };

  private mUploadingListener = (localAddress: string, progress: number) => {
    if (this.knownLocalAddress !== localAddress) {
      return;
    }
    this.trace(`mUploadingListener | progress: ${progress}`);
    this.progress = progress;
    this.emitStateChange();
  };

  private mUploadErrorListener = (localAddress: string, error: any) => {
    if (this.knownLocalAddress !== localAddress) {
      return;
    }
    this.emit('error', `mUploadErrorListener | error: ${error?.message || error}`, this);
    this.progress = 0;
    this.emitStateChange();
  };

  private mMovedListener = (previousAddress: string, newAddress: string) => {
    if (this.knownLocalAddress !== previousAddress) {
      return;
    }
    this.trace(`mMovedListener | from: ${previousAddress} to: ${newAddress}`);
    this.knownLocalAddress = newAddress;
    this.emitStateChange();
  };

  private mEvictionListener = (localAddress: string, reason) => {
    if (this.knownLocalAddress !== localAddress && localAddress !== 'all') {
      return;
    }
    this.trace(`mEvictionListener | target: ${localAddress}`);
    this.release(reason, true);
  };

  public isReleased() {
    return this.released;
  }

  private retain() {
    this.references += 1;
    this.trace(`retain() | references: ${this.references}`);
  }

  public release(reason: string, force = false) {
    if (this.isReleased()) {
      return;
    }
    this.references -= 1;
    this.trace(`release() | references: ${this.references} | force=${force} | uploadable=${this.isUploadable()}`);
    if (this.isUploadable()) {
      return;
    }
    if (this.references > 0 && !force) {
      return;
    }

    this.released = true;
    this.unregisterListeners();
    this.inited = false;
    this.emit('evicted', reason);

    if (this.cacheKey) {
      FileAsset.cache.delete(this.cacheKey);
    }

    if (!this.knownLocalAddress) {
      return;
    }

    const lastAddress = this.knownLocalAddress;
    this.knownLocalAddress = undefined;
    this.storage.released(lastAddress, reason);
  }

  private registerListeners() {
    this.storage
      .on('download-error', this.mDownloadErrorListener)
      .on('upload-error', this.mUploadErrorListener)
      .on('uploaded', this.mUploadedListener)
      .on('downloaded', this.mDownloadedListener)
      .on('uploading', this.mUploadingListener)
      .on('downloading', this.mDownloadingListener)
      .on('moved', this.mMovedListener)
      .on('evicted', this.mEvictionListener);
  }

  private unregisterListeners() {
    this.storage
      .off('download-error', this.mDownloadErrorListener)
      .off('upload-error', this.mUploadErrorListener)
      .off('uploaded', this.mUploadedListener)
      .off('downloaded', this.mDownloadedListener)
      .off('uploading', this.mUploadingListener)
      .off('downloading', this.mDownloadingListener)
      .off('moved', this.mMovedListener)
      .off('evicted', this.mEvictionListener);
  }
}
